import { useMemo, useState } from 'react'
import {
  EmptyRow, ErpTable, KpiStrip, MetricCard, Panel, Td, Th,
} from '@/components/erp/primitives'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { useLoad } from '@/features/administration/hooks'
import { fmtNumber, humanise } from '@/shared/lib/format'
import { cn } from '@/lib/utils'
import type {
  ConcentrationResponse, EcosystemNodeTypeCode, EcosystemResponse,
} from '@/shared/api/types'
import { analyticsConcentration, analyticsEcosystem } from '../api'
import { Truncation } from '../parts'
import { NODE_FILL, NodeTypeBadge, edgeTypeLabel, nodeTypeLabel } from '../vocab'
import { AnalyticsScreen, useAnalyticsFilters } from './shell'

/**
 * Who else is in the room: the partners, channels, mobilizers and units a
 * programme runs through.
 *
 * ## The layout is deterministic, not a force simulation
 *
 * A force-directed graph settles somewhere different every time it is opened,
 * which makes "it moved" impossible to distinguish from "the data changed", and
 * it makes a screenshot in a report unreproducible. Nodes are laid out in
 * labelled columns by type, ordered by weight. Two people opening this screen on
 * the same data see the same picture.
 *
 * ## Colour is reinforcement here, not the encoding
 *
 * The type fills are the app's chart tokens, which are deliberately warm and
 * close together and do not survive a colour-vision check as an identity
 * channel. So type is carried by the COLUMN and by the column's heading; the
 * fill agrees with it. Nobody has to tell two of these hues apart to read the
 * graph.
 */
export function ProgramEcosystemScreen() {
  return (
    <AnalyticsScreen
      title="Ecosystem"
      description="How programmes connect to the partners, channels, mobilizers and units that deliver them."
      load={(f) => analyticsEcosystem(f)}
      isEmpty={(d) => (d.nodes?.length ?? 0) === 0}
    >
      {(d) => <EcosystemBody d={d} />}
    </AnalyticsScreen>
  )
}

const COLUMN_ORDER: EcosystemNodeTypeCode[] = [
  'PARTNER', 'PROGRAMME', 'MOBILIZER', 'CHANNEL', 'UNIT', 'CANDIDATE', 'PARTY',
]

const GRAPH_H = 380

function EcosystemBody({ d }: { d: EcosystemResponse }) {
  const [focus, setFocus] = useState<string | null>(null)

  const columns = useMemo(() => {
    const byType = new Map<string, typeof d.nodes>()
    ;(d.nodes ?? []).forEach((n) => {
      const list = byType.get(n.nodeTypeCode) ?? []
      list.push(n)
      byType.set(n.nodeTypeCode, list)
    })
    return COLUMN_ORDER
      .filter((t) => (byType.get(t)?.length ?? 0) > 0)
      .map((t) => ({
        type: t,
        nodes: [...byType.get(t)!].sort((a, b) => b.weight - a.weight),
      }))
  }, [d.nodes])

  // Position is a pure function of (column index, rank within column) — same
  // data in, same picture out.
  const pos = useMemo(() => {
    const m = new Map<string, { x: number; y: number }>()
    columns.forEach((col, ci) => {
      const x = columns.length === 1 ? 50 : 8 + (ci / (columns.length - 1)) * 84
      col.nodes.forEach((n, ni) => {
        const y = col.nodes.length === 1 ? 50 : 10 + (ni / (col.nodes.length - 1)) * 80
        m.set(n.nodeId, { x, y })
      })
    })
    return m
  }, [columns])

  // An edge whose endpoint was dropped by the server's node cap cannot be drawn.
  // How many that is goes on the card below rather than vanishing, because
  // "Connections 28" over a graph that had 34 is a count of what was DRAWN
  // presented as a count of what exists.
  const allEdges = d.edges ?? []
  const edges = allEdges.filter((e) => pos.has(e.sourceId) && pos.has(e.targetId))
  const undrawableEdges = allEdges.length - edges.length
  const maxEdge = Math.max(...edges.map((e) => e.weight), 1)
  const maxNode = Math.max(...(d.nodes ?? []).map((n) => n.weight), 1)

  const connected = useMemo(() => {
    if (!focus) return null
    const s = new Set<string>([focus])
    edges.forEach((e) => {
      if (e.sourceId === focus) s.add(e.targetId)
      if (e.targetId === focus) s.add(e.sourceId)
    })
    return s
  }, [focus, edges])

  const focusNode = (d.nodes ?? []).find((n) => n.nodeId === focus)

  return (
    <div className="space-y-4">
      <KpiStrip>
        <MetricCard label="Nodes" value={fmtNumber(d.nodes?.length ?? 0)}
          sub={`${columns.length} kinds of thing`} />
        <MetricCard label="Connections" value={fmtNumber(edges.length)}
          sub={undrawableEdges > 0
            ? `${fmtNumber(undrawableEdges)} more reach a node the cap dropped`
            : undefined} />
        <MetricCard label="Cap" value={fmtNumber(d.cap)}
          sub="Nodes and edges are capped per response" />
        <MetricCard label="Dropped by the cap"
          value={fmtNumber((d.truncatedNodes ?? 0) + (d.truncatedEdges ?? 0))}
          sub={`${fmtNumber(d.truncatedNodes ?? 0)} nodes · ${fmtNumber(d.truncatedEdges ?? 0)} edges`} />
      </KpiStrip>

      <Truncation truncatedTo={d.nodes?.length ?? 0}
        totalAvailable={(d.nodes?.length ?? 0) + (d.truncatedNodes ?? 0)}
        orderedBy="weight" noun="nodes" />

      <Panel
        title="Delivery graph"
        description="Click a node to isolate what it touches. Line thickness is the weight the server sent; nothing here is inferred in the browser."
      >
        <div className="relative w-full" style={{ height: GRAPH_H }}>
          <svg
            className="absolute inset-0 h-full w-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden
          >
            {edges.map((e, i) => {
              const a = pos.get(e.sourceId)!
              const b = pos.get(e.targetId)!
              const dim = connected != null && !(connected.has(e.sourceId) && connected.has(e.targetId))
              return (
                <line
                  key={`${e.sourceId}-${e.targetId}-${i}`}
                  x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                  stroke="var(--muted-foreground)"
                  strokeOpacity={dim ? 0.06 : 0.18 + (e.weight / maxEdge) * 0.3}
                  strokeWidth={1 + (e.weight / maxEdge) * 2}
                  // Without this the non-uniform viewBox scaling would make a
                  // horizontal line hairline-thin and a vertical one heavy.
                  vectorEffect="non-scaling-stroke"
                />
              )
            })}
          </svg>

          {columns.map((col, ci) => (
            <div
              key={col.type}
              className="absolute -translate-x-1/2 text-erp-xs font-medium uppercase tracking-wide text-muted-foreground whitespace-nowrap"
              style={{ left: `${columns.length === 1 ? 50 : 8 + (ci / (columns.length - 1)) * 84}%`, top: 0 }}
            >
              {nodeTypeLabel(col.type)}
            </div>
          ))}

          {(d.nodes ?? []).map((n) => {
            const p = pos.get(n.nodeId)
            if (!p) return null
            const size = 10 + Math.sqrt(n.weight / maxNode) * 14
            const dim = connected != null && !connected.has(n.nodeId)
            return (
              <button
                key={n.nodeId}
                type="button"
                className={cn(
                  'absolute -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-card transition-opacity',
                  dim ? 'opacity-20' : 'opacity-95',
                )}
                style={{
                  left: `${p.x}%`, top: `${p.y}%`, width: size, height: size,
                  background: NODE_FILL[n.nodeTypeCode] ?? 'var(--muted-foreground)',
                }}
                onClick={() => setFocus((f) => (f === n.nodeId ? null : n.nodeId))}
                aria-pressed={focus === n.nodeId}
                aria-label={`${nodeTypeLabel(n.nodeTypeCode)}: ${n.label}, weight ${n.weight}`}
                title={`${nodeTypeLabel(n.nodeTypeCode)} · ${n.label} · weight ${fmtNumber(n.weight)}`}
              />
            )
          })}
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-erp-xs text-muted-foreground">
          {columns.map((c) => (
            <span key={c.type} className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full" style={{ background: NODE_FILL[c.type] }} />
              {nodeTypeLabel(c.type)} · {c.nodes.length}
            </span>
          ))}
        </div>

        {focusNode && (
          <div className="mt-3 rounded-md border border-border bg-muted/30 p-3">
            <div className="flex items-center gap-2">
              <NodeTypeBadge code={focusNode.nodeTypeCode} />
              <span className="font-medium">{focusNode.label}</span>
              <button type="button" className="erp-btn-ghost ml-auto text-erp-xs"
                onClick={() => setFocus(null)}>Clear</button>
            </div>
            <ul className="mt-2 space-y-1 text-erp-xs text-muted-foreground">
              {edges.filter((e) => e.sourceId === focus || e.targetId === focus).map((e, i) => {
                const otherId = e.sourceId === focus ? e.targetId : e.sourceId
                const other = d.nodes.find((n) => n.nodeId === otherId)
                return (
                  <li key={i}>
                    {edgeTypeLabel(e.edgeTypeCode)} · {other?.label ?? otherId}
                    <span className="text-muted-foreground/70"> (weight {fmtNumber(e.weight)})</span>
                  </li>
                )
              })}
            </ul>
          </div>
        )}
      </Panel>

      <Note tone="info">
        A CHANNEL node has no record behind it — it is a delivery method, so there is
        nothing to open. Programme, partner, mobilizer and unit nodes each correspond to a
        row somewhere in the system.
      </Note>

      <ConcentrationPanel />
    </div>
  )
}

/**
 * Where the portfolio has put all its eggs.
 *
 * The reason code is the server's, unchanged. A partner delivering nine of ten
 * programmes may be the only capable partner in that county; the screen says how
 * concentrated the portfolio is and stops there.
 */
function ConcentrationPanel() {
  const f = useAnalyticsFilters()
  // Depends on the filter, like every other panel. With an empty dep array this
  // fetched once per mount and then sat there contradicting the graph above it
  // whenever anybody touched a chip.
  const { data, error, loading } = useLoad<ConcentrationResponse>(
    () => analyticsConcentration(f.query), [JSON.stringify(f.query)],
  )

  if (loading && !data) return <Panel title="Concentration"><LoadingBlock label="Loading concentration…" /></Panel>
  if (error) return <Panel title="Concentration"><ErrorStrip message={error} /></Panel>
  if (!data) return null

  return (
    <Panel
      title="Concentration"
      description="Things the portfolio depends on more than once. A count, not a judgement."
      bodyClassName="p-0"
    >
      <ErpTable head={(
        <>
          <Th>Kind</Th>
          <Th>Name</Th>
          <Th className="text-right">Programmes</Th>
          <Th>Why it is listed</Th>
        </>
      )}>
        {(data.rows?.length ?? 0) === 0
          ? <EmptyRow colSpan={4} message="Nothing in this portfolio is concentrated enough to list." />
          : data.rows.map((r, i) => (
            <tr key={`${r.nodeTypeCode}-${r.entityId ?? r.label}-${i}`}>
              <Td><NodeTypeBadge code={r.nodeTypeCode} /></Td>
              <Td className="font-medium">{r.label}</Td>
              <Td className="text-right tabular-nums">{fmtNumber(r.programmeCount)}</Td>
              <Td className="text-erp-xs text-muted-foreground">{humanise(r.reasonCode)}</Td>
            </tr>
          ))}
      </ErpTable>
    </Panel>
  )
}
